import { useRef, useState, useCallback } from 'react';

// --- Constants ---
const W = 1080;
const H = 1920;
const FPS = 30;
const SLIDE_DURATION = 3.3; // seconds per slide
const FADE_IN = 0.3;
const TYPE_DURATION = 0.8;
const BODY_FADE = 0.5;
const FADE_OUT = 0.5;

const SLIDE_COLORS = ['#0F0F14', '#1A0A0F', '#0F172A', '#18181B', '#0F0F14', '#E11D48'];
const SLIDE_LABELS = ['', 'THE PROBLEM', 'THE DATA', 'THE GAP', 'THE SOLUTION', 'HEMOLOGIX.AI'];
const ACCENT = '#E11D48';

// --- Helpers ---

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function drawGrid(ctx: CanvasRenderingContext2D) {
  ctx.save();
  ctx.globalAlpha = 0.04;
  ctx.strokeStyle = 'rgba(255,255,255,0.3)';
  ctx.lineWidth = 1;
  const gap = 60;
  for (let x = 0; x < W; x += gap) {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
  }
  for (let y = 0; y < H; y += gap) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
  }
  ctx.restore();
}

function drawHemologixBadge(ctx: CanvasRenderingContext2D, alpha: number) {
  ctx.save();
  ctx.globalAlpha = alpha * 0.5;
  const bx = 80, by = H - 80;
  // Pink H square
  const grad = ctx.createLinearGradient(bx, by - 32, bx + 32, by);
  grad.addColorStop(0, '#E11D48');
  grad.addColorStop(1, '#FB7185');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.roundRect(bx, by - 32, 32, 32, 4);
  ctx.fill();
  ctx.fillStyle = '#FFFFFF';
  ctx.font = 'bold 16px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('H', bx + 16, by - 10);
  // HEMOLOGIX text
  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.font = 'bold 16px Arial, sans-serif';
  ctx.textAlign = 'left';
  ctx.letterSpacing = '2px';
  ctx.fillText('HEMOLOGIX', bx + 42, by - 10);
  ctx.restore();
}

function drawProgressBar(ctx: CanvasRenderingContext2D, progress: number) {
  ctx.save();
  // Background
  ctx.fillStyle = 'rgba(255,255,255,0.1)';
  ctx.fillRect(0, H - 6, W, 6);
  // Fill
  ctx.fillStyle = ACCENT;
  ctx.fillRect(0, H - 6, W * progress, 6);
  ctx.restore();
}

interface SlideData {
  title: string;
  body: string;
}

function drawFrame(
  ctx: CanvasRenderingContext2D,
  slides: SlideData[],
  totalElapsed: number,
  totalDuration: number,
) {
  const slideIndex = Math.min(Math.floor(totalElapsed / SLIDE_DURATION), slides.length - 1);
  const slideTime = totalElapsed - slideIndex * SLIDE_DURATION;
  const slide = slides[slideIndex];
  const isLast = slideIndex === slides.length - 1;

  // --- Background ---
  if (isLast) {
    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, ACCENT);
    grad.addColorStop(1, '#9F1239');
    ctx.fillStyle = grad;
  } else {
    ctx.fillStyle = SLIDE_COLORS[slideIndex % SLIDE_COLORS.length];
  }
  ctx.fillRect(0, 0, W, H);

  // --- Grid ---
  drawGrid(ctx);

  // --- Calculate alphas ---
  // Fade in: 0 → FADE_IN
  const fadeInAlpha = Math.min(1, slideTime / FADE_IN);
  // Fade out: starts at SLIDE_DURATION - FADE_OUT
  const fadeOutStart = SLIDE_DURATION - FADE_OUT;
  const fadeOutAlpha = slideTime > fadeOutStart && !isLast
    ? 1 - Math.min(1, (slideTime - fadeOutStart) / FADE_OUT)
    : 1;
  const masterAlpha = easeInOut(fadeInAlpha) * fadeOutAlpha;

  // --- Slide label (top-left) ---
  const label = SLIDE_LABELS[slideIndex] || '';
  if (label) {
    ctx.save();
    ctx.globalAlpha = masterAlpha * 0.8;
    ctx.fillStyle = isLast ? 'rgba(255,255,255,0.6)' : ACCENT;
    ctx.font = 'bold 18px Arial, sans-serif';
    ctx.letterSpacing = '3px';
    ctx.textAlign = 'left';
    ctx.fillText(label, 80, 120);
    ctx.restore();
  }

  // --- Title (typewriter) ---
  const typeProgress = Math.min(1, Math.max(0, (slideTime - FADE_IN) / TYPE_DURATION));
  const visibleChars = Math.floor(slide.title.length * easeInOut(typeProgress));
  const titleText = slide.title.substring(0, visibleChars);

  ctx.save();
  ctx.globalAlpha = masterAlpha;
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '400 72px Georgia, "Times New Roman", serif';
  ctx.textAlign = 'left';
  const titleLines = wrapText(ctx, titleText, W - 160);
  const titleY = H * 0.35;
  titleLines.forEach((line, i) => {
    ctx.fillText(line, 80, titleY + i * 88);
  });
  ctx.restore();

  // --- Body (fade in after title) ---
  const bodyStart = FADE_IN + TYPE_DURATION;
  const bodyAlpha = Math.min(1, Math.max(0, (slideTime - bodyStart) / BODY_FADE));

  ctx.save();
  ctx.globalAlpha = masterAlpha * easeInOut(bodyAlpha) * 0.75;
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '400 36px Arial, sans-serif';
  ctx.textAlign = 'left';
  const bodyLines = wrapText(ctx, slide.body, W - 160);
  const bodyY = titleY + titleLines.length * 88 + 40;
  bodyLines.forEach((line, i) => {
    ctx.fillText(line, 80, bodyY + i * 52);
  });
  ctx.restore();

  // --- Badge ---
  drawHemologixBadge(ctx, masterAlpha);

  // --- Progress bar ---
  drawProgressBar(ctx, totalElapsed / totalDuration);
}

// --- Component ---

interface TikTokVideoMakerProps {
  slides: SlideData[];
}

export function TikTokVideoMaker({ slides }: TikTokVideoMakerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [recording, setRecording] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const animFrameRef = useRef(0);
  const recorderRef = useRef<MediaRecorder | null>(null);

  const C = {
    accent: '#E11D48',
    bg: '#0F0F14',
    bgCard: '#1A1A24',
    border: '#2A2A3A',
    text: '#F1F1F4',
    textSec: '#A0A0B8',
    textMuted: '#6B6B80',
  };
  const B = { fontFamily: "'Inter', system-ui, -apple-system, sans-serif" };

  const generate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || slides.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Reset
    setVideoUrl(null);
    setRecording(true);
    setProgress(0);

    const totalDuration = slides.length * SLIDE_DURATION;
    const stream = canvas.captureStream(FPS);
    const chunks: Blob[] = [];

    // Try VP9 first, fall back to VP8
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : 'video/webm;codecs=vp8';

    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 4_000_000 });
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: mimeType });
      const url = URL.createObjectURL(blob);
      setVideoUrl(url);
      setRecording(false);
    };

    recorder.start(100); // collect chunks every 100ms

    const startTime = performance.now();

    const animate = () => {
      const elapsed = (performance.now() - startTime) / 1000;
      if (elapsed >= totalDuration) {
        // Draw final frame
        drawFrame(ctx, slides, totalDuration - 0.01, totalDuration);
        recorder.stop();
        setProgress(1);
        return;
      }

      drawFrame(ctx, slides, elapsed, totalDuration);
      setProgress(elapsed / totalDuration);
      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);
  }, [slides]);

  const downloadVideo = useCallback(() => {
    if (!videoUrl) return;
    const a = document.createElement('a');
    a.href = videoUrl;
    a.download = `hemologix-tiktok-${Date.now()}.webm`;
    a.click();
  }, [videoUrl]);

  if (slides.length === 0) return null;

  return (
    <div style={{ background: C.bgCard, borderRadius: 14, border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>{'\u{1F3AC}'}</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.text, ...B }}>TikTok Video</span>
          <span style={{ fontSize: 11, color: C.textMuted, ...B }}>20s</span>
        </div>
        {videoUrl && (
          <span style={{ fontSize: 10, fontWeight: 700, color: '#10B981', background: '#ECFDF5', padding: '2px 8px', borderRadius: 6, ...B }}>READY</span>
        )}
      </div>

      {/* Canvas preview */}
      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: '100%',
          maxWidth: 240,
          aspectRatio: '9 / 16',
          borderRadius: 12,
          overflow: 'hidden',
          border: `1px solid ${C.border}`,
          background: '#000',
        }}>
          <canvas
            ref={canvasRef}
            width={W}
            height={H}
            style={{ width: '100%', height: '100%', display: 'block' }}
          />
        </div>

        {/* Progress bar during recording */}
        {recording && (
          <div style={{ width: '100%', maxWidth: 240 }}>
            <div style={{ height: 4, background: C.border, borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', background: C.accent, borderRadius: 2, width: `${progress * 100}%`, transition: 'width 0.1s' }} />
            </div>
            <p style={{ fontSize: 11, color: C.textMuted, textAlign: 'center', margin: '6px 0 0', ...B }}>
              Recording... {Math.round(progress * 100)}%
            </p>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, width: '100%', maxWidth: 240 }}>
          {!videoUrl ? (
            <button
              onClick={generate}
              disabled={recording}
              style={{
                flex: 1,
                background: recording ? C.border : C.accent,
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                padding: '10px 16px',
                fontSize: 13,
                fontWeight: 700,
                cursor: recording ? 'default' : 'pointer',
                opacity: recording ? 0.7 : 1,
                ...B,
              }}
            >
              {recording ? 'Recording...' : 'Generate TikTok'}
            </button>
          ) : (
            <>
              <button
                onClick={downloadVideo}
                style={{
                  flex: 1,
                  background: C.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  ...B,
                }}
              >
                Download Video
              </button>
              <button
                onClick={generate}
                style={{
                  background: C.bg,
                  color: C.textSec,
                  border: `1.5px solid ${C.border}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  ...B,
                }}
              >
                Redo
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
